import { useState } from "react";
import {
  loadDailyEnergy,
  getEnergyAdvice,
  getTimeContextRecommendation,
  calculateROI,
  recordMealGI,
  removeMeal,
  getGLTarget,
  type DailyEnergy,
  type EnergyAdvice,
  type MealEntry,
} from "../lib/energyTracker";
import { trackMealLogged, trackDailyProgress, trackEnergyPortfolioExpanded } from "../lib/analytics";

const QUICK_LOG_OPTIONS: { label: string; gi: number; gl: number; context: MealEntry["context"] }[] = [
  { label: "Eggs & avocado", gi: 0, gl: 0, context: "breakfast" },
  { label: "Oatmeal (1 cup)", gi: 55, gl: 13, context: "breakfast" },
  { label: "Toast & cereal", gi: 72, gl: 18, context: "breakfast" },
  { label: "Salad + protein", gi: 15, gl: 3, context: "lunch" },
  { label: "Sandwich", gi: 50, gl: 12, context: "lunch" },
  { label: "Fast food meal", gi: 75, gl: 28, context: "lunch" },
  { label: "Grilled meat & veggies", gi: 15, gl: 4, context: "dinner" },
  { label: "Pasta (1 cup)", gi: 55, gl: 23, context: "dinner" },
  { label: "Rice dish (1 cup)", gi: 73, gl: 29, context: "dinner" },
  { label: "Nuts/cheese", gi: 0, gl: 0, context: "snack" },
  { label: "Apple or orange", gi: 40, gl: 5, context: "snack" },
  { label: "Chips/crackers", gi: 70, gl: 15, context: "snack" },
];

function getGLPillClass(gl: number): string {
  if (gl <= 10) return "gi-pill gi-pill--low";
  if (gl <= 19) return "gi-pill gi-pill--medium";
  return "gi-pill gi-pill--high";
}

function computeInitialState() {
  const data = loadDailyEnergy();
  return {
    energy: data,
    advice: getEnergyAdvice(data),
    roi: calculateROI(data),
    timeRec: getTimeContextRecommendation(),
  };
}

export default function EnergyROI() {
  const [initial] = useState(computeInitialState);
  const [energy, setEnergy] = useState<DailyEnergy | null>(initial.energy);
  const [advice, setAdvice] = useState<EnergyAdvice | null>(initial.advice);
  const [roi, setRoi] = useState(initial.roi);
  const [timeRec, setTimeRec] = useState(initial.timeRec);
  const [expanded, setExpanded] = useState(false);
  const [showExplainer, setShowExplainer] = useState(false);
  const [customMeal, setCustomMeal] = useState("");
  const [customGL, setCustomGL] = useState(10);

  const refreshData = () => {
    const data = loadDailyEnergy();
    setEnergy(data);
    const newAdvice = getEnergyAdvice(data);
    setAdvice(newAdvice);
    const newRoi = calculateROI(data);
    setRoi(newRoi);
    setTimeRec(getTimeContextRecommendation());

    if (data.meals.length > 0) {
      trackDailyProgress({
        totalGLPoints: data.totalGLPoints || 0,
        mealsLogged: data.meals.length,
        roiPercentage: newRoi.percentage,
        budgetStatus: data.balance,
      });
    }
  };

  const handleQuickLog = (option: typeof QUICK_LOG_OPTIONS[0]) => {
    recordMealGI(option.label, option.gi, option.context, option.gl);
    trackMealLogged({
      mealDescription: option.label,
      gi: option.gi,
      gl: option.gl,
      context: option.context,
      isQuickLog: true,
    });
    refreshData();
  };

  const handleRemoveMeal = (mealId: string) => {
    removeMeal(mealId);
    refreshData();
  };

  const handleCustomLog = () => {
    if (!customMeal.trim()) return;
    const context: MealEntry["context"] =
      new Date().getHours() < 10 ? "breakfast" :
      new Date().getHours() < 14 ? "lunch" :
      new Date().getHours() < 18 ? "snack" : "dinner";
    const estimatedGI = Math.round(customGL * 3);
    recordMealGI(customMeal, estimatedGI, context, customGL);
    trackMealLogged({
      mealDescription: customMeal,
      gi: estimatedGI,
      gl: customGL,
      context: context,
      isQuickLog: false,
    });
    setCustomMeal("");
    setCustomGL(10);
    refreshData();
  };

  if (!energy || !advice) return null;

  const totalGL = energy.totalGLPoints || 0;
  const glTarget = getGLTarget();
  const budgetUsed = Math.min(100, (totalGL / glTarget) * 100);

  return (
    <div>
      <div className="section-label">Energy Budget</div>

      {/* Summary — always visible */}
      <button
        className="w-full text-left"
        onClick={() => {
          if (!expanded) trackEnergyPortfolioExpanded();
          setExpanded(!expanded);
        }}
      >
        <div className="flex items-baseline justify-between mb-2">
          <div>
            <span className="text-2xl font-semibold text-text font-[family-name:--font-ui]">{totalGL}</span>
            <span className="text-xs text-muted font-[family-name:--font-ui] ml-1">/ {glTarget} GL</span>
          </div>
          <span className="text-xs text-muted font-[family-name:--font-ui]">{roi.label}</span>
        </div>

        {/* Budget Bar */}
        <div className="h-1.5 bg-border rounded-full overflow-hidden mb-1">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              budgetUsed < 80 ? "bg-gi-low" :
              budgetUsed < 100 ? "bg-gi-medium" : "bg-gi-high"
            }`}
            style={{ width: `${budgetUsed}%` }}
          />
        </div>
        <div className="flex justify-between text-[11px] text-muted font-[family-name:--font-ui]">
          <span>{energy.meals.length} meal{energy.meals.length !== 1 ? "s" : ""} logged</span>
          <span>{Math.max(0, glTarget - totalGL)} GL remaining</span>
        </div>

        <div className="flex justify-center mt-2">
          <svg
            className={`w-4 h-4 text-muted transition-transform ${expanded ? "rotate-180" : ""}`}
            fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded Section */}
      {expanded && (
        <div className="mt-4 space-y-4">
          {/* Strategy & Time Context */}
          <div>
            <p className="text-xs text-muted font-[family-name:--font-ui] font-medium mb-1">Strategy</p>
            <p className="text-sm text-text">{advice.recommendation}</p>
          </div>
          <div>
            <p className="text-xs text-muted font-[family-name:--font-ui] font-medium mb-1">Right now</p>
            <p className="text-sm text-text">{timeRec}</p>
          </div>

          {/* Explainer */}
          <button
            onClick={(e) => { e.stopPropagation(); setShowExplainer(!showExplainer); }}
            className="text-xs text-muted font-[family-name:--font-ui] hover:text-text transition-colors"
          >
            {showExplainer ? "Hide" : "What is GL?"}
          </button>
          {showExplainer && (
            <div className="text-xs text-muted leading-relaxed space-y-1.5 border-l-2 border-border pl-3">
              <p><strong className="text-text">Glycemic Load (GL)</strong> measures a food's real blood sugar impact — it combines how fast a food raises blood sugar (GI) with how much carb is in a serving.</p>
              <p>A daily GL under {glTarget} is considered a low-GL diet. You can adjust your target in Settings &gt; Preferences.</p>
              <p className="text-muted/70">Low GL (0–10) per food = minimal impact. Medium (11–19) = moderate. High (20+) = significant.</p>
            </div>
          )}

          {/* Today's Meals */}
          {energy.meals.length > 0 && (
            <div className="border-t border-border-light pt-3">
              <p className="text-xs text-muted font-[family-name:--font-ui] font-medium mb-2">Today&apos;s meals</p>
              <div className="space-y-1">
                {energy.meals.map((meal) => {
                  const gl = meal.estimatedGL ?? Math.round(meal.estimatedGI * 0.15);
                  return (
                    <div key={meal.id} className="flex items-center justify-between text-sm group">
                      <span className="text-text truncate flex-1">{meal.description}</span>
                      <div className="flex items-center gap-2 ml-2">
                        <span className={getGLPillClass(gl)}>GL {gl}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRemoveMeal(meal.id); }}
                          className="opacity-0 group-hover:opacity-100 text-muted hover:text-gi-high transition-opacity w-5 h-5 flex items-center justify-center flex-shrink-0"
                          title="Remove"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quick Log */}
          <div className="border-t border-border-light pt-3">
            <p className="text-xs text-muted font-[family-name:--font-ui] font-medium mb-3">Log a meal</p>

            <div className="grid grid-cols-3 gap-2 mb-4">
              {QUICK_LOG_OPTIONS.map((option, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); handleQuickLog(option); }}
                  className="p-2 border border-border rounded-md text-left hover:bg-black/5 active:bg-black/10 transition-colors"
                >
                  <div className="text-xs text-text truncate">{option.label}</div>
                  <div className={`text-[10px] font-[family-name:--font-ui] font-medium ${
                    option.gl <= 10 ? "text-gi-low" : option.gl <= 19 ? "text-gi-medium" : "text-gi-high"
                  }`}>
                    GL {option.gl}
                  </div>
                </button>
              ))}
            </div>

            {/* Custom entry */}
            <div className="border-t border-border-light pt-3">
              <p className="text-[11px] text-muted font-[family-name:--font-ui] mb-2">Custom meal (GL = glycemic load per serving):</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={customMeal}
                  onChange={(e) => setCustomMeal(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Describe your meal..."
                  className="flex-1 px-3 py-2 border border-border rounded-md text-sm bg-bg text-text outline-none focus:border-accent"
                />
                <div className="flex items-center gap-1 px-2 py-1 border border-border rounded-md">
                  <input
                    type="number"
                    min="0"
                    max="50"
                    value={customGL}
                    onChange={(e) => setCustomGL(Number(e.target.value))}
                    onClick={(e) => e.stopPropagation()}
                    className="w-10 text-center text-sm font-medium bg-transparent outline-none font-[family-name:--font-ui]"
                  />
                  <span className="text-xs text-muted font-[family-name:--font-ui]">GL</span>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); handleCustomLog(); }}
                  disabled={!customMeal.trim()}
                  className="px-3 py-2 bg-accent text-white rounded-md text-sm font-[family-name:--font-ui] font-medium disabled:opacity-50 hover:opacity-85 active:opacity-75 transition-opacity"
                >
                  Log
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick action when collapsed */}
      {!expanded && (
        <button
          onClick={() => { trackEnergyPortfolioExpanded(); setExpanded(true); }}
          className="w-full mt-3 py-2 text-sm text-accent font-[family-name:--font-ui] font-medium hover:underline"
        >
          Log a meal
        </button>
      )}
    </div>
  );
}
